import React, { useState } from 'react';
import { Badge, Button, Popover, Box } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ModuleColumn } from '../../types';
import { brandColors } from '../../theme/appTheme';
import { tableActionButtonSx } from '../../theme/formStyles';
import { TableColumnEditor } from './TableColumnEditor';

interface TableColumnPickerProps {
  allColumns: ModuleColumn[];
  visibleFields: string[];
  defaultFields: string[];
  onSave: (fields: string[]) => void | Promise<void>;
  onReset?: () => void | Promise<void>;
  isCustomized?: boolean;
}

export const TableColumnPicker: React.FC<TableColumnPickerProps> = ({
  allColumns,
  visibleFields,
  defaultFields,
  onSave,
  onReset,
  isCustomized,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);
  const hiddenCount = allColumns.length - visibleFields.length;
  const badgeValue = isCustomized ? '!' : (hiddenCount > 0 ? hiddenCount : 0);

  const handleSave = async (fields: string[]) => {
    try {
      setSaving(true);
      await onSave(fields);
      setAnchor(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Badge
        badgeContent={badgeValue}
        color="primary"
        invisible={!badgeValue}
        overlap="rectangular"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          overflow: 'visible',
          '& .MuiBadge-badge': {
            fontSize: 9,
            height: 16,
            minWidth: 16,
            top: 4,
            right: 4,
          },
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            ...tableActionButtonSx,
            height: 32,
            minHeight: 32,
            borderColor: brandColors.warning,
            color: brandColors.warning,
            bgcolor: '#fff',
            '&:hover': {
              borderColor: brandColors.warning,
              bgcolor: 'rgba(245,158,11,0.08)',
              color: brandColors.warning,
            },
          }}
        >
          Columns
        </Button>
      </Badge>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: 320, borderRadius: 3, boxShadow: '0 16px 48px rgba(15,23,42,0.15)' } } }}
      >
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
          <TableColumnEditor
            compact
            allColumns={allColumns}
            visibleFields={visibleFields}
            defaultFields={defaultFields}
            onSave={handleSave}
            onReset={onReset}
            saving={saving}
          />
        </Box>
      </Popover>
    </>
  );
};
